/**
 * Hook encapsulating AI text generation state + handlers.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState, useCallback } from 'react';
import { useGenerateTexts } from '@/features/campaign-texts/hooks/useGenerateTexts';
import { useGenerationPromptPreview } from '@/features/campaign-texts/hooks/useGenerationPromptPreview';
import { useTranslateTexts } from '@/features/campaign-texts/hooks/useTranslateTexts';
import { formatGenerationPromptForClipboard } from '@/features/campaign-texts/utils/format-generation-prompt-clipboard';
import { isSmsTextOverLimit } from '@/features/campaign-texts/smsTextLimit';
import type {
  AiGeneratedTextMetadata,
  AiGenerateMode,
} from '@/features/campaign-texts/components/AiGenerateDropdown';
import type { AiTextMetadataPayload } from '@/hooks';
import { getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';

import { resolveAiGeneratePreviewParams } from './resolve-ai-generate-preview-params';

function buildDropIndicesOverSmsLimit(
  batches: string[][],
): Array<{ dropIdx: number; indices: number[] }> {
  const out: Array<{ dropIdx: number; indices: number[] }> = [];
  batches.forEach((batch, dropIdx) => {
    const indices = batch
      .map((t, i) => (isSmsTextOverLimit(t) ? i : -1))
      .filter((i) => i >= 0);
    if (indices.length > 0) out.push({ dropIdx, indices });
  });
  return out;
}

function buildIndicesOverSmsLimit(texts: string[]): number[] {
  return texts
    .map((t, i) => (isSmsTextOverLimit(t) ? i : -1))
    .filter((i) => i >= 0);
}

interface AiTextGenerationParams {
  countryKey: string;
  onAddTexts: (texts: string[], aiTextMetadata?: AiTextMetadataPayload[]) => Promise<void>;
  /** Adds texts, selects ONLY them, marks old selection as replaced. */
  onSaveGeneratedAndSelectOnly?: (texts: string[]) => void;
  onMarkTextsForHide?: (texts: string[]) => void;
  /** For per-drop mode: saves each batch to its drop. */
  onSaveGeneratedPerDrop?: (batches: string[][]) => Promise<void>;
  /** Called when per-drop Use & Hide is applied – stores per-list SMS texts for draft creation. */
  onPerDropSmsTextsApplied?: (batches: string[][]) => void;
  /** Called when AI generation completes – deselects non-generated SMS texts by default. */
  onAiGeneratedDeselectOld?: () => void;
  /** Called after Save or Use & Hide — collapse texts, give space to senders. */
  onAfterSave?: () => void;
  /** Called after Use & Hide to set visible=false immediately (bulk edit needs this). */
  onHideTextsAfterUseAndHide?: (texts: string[]) => Promise<void>;
}

function withTextMetadata(
  texts: string[],
  metadata?: AiGeneratedTextMetadata | null,
): AiTextMetadataPayload[] | undefined {
  if (!metadata) return undefined;
  return texts.map((text) => ({ text, ...metadata }));
}

function perDropTextMetadata(
  batches: string[][],
  metadata: AiGeneratedTextMetadata[],
): AiTextMetadataPayload[] | undefined {
  const items = batches.flatMap((batch, dropIdx) => withTextMetadata(batch, metadata[dropIdx]) ?? []);
  return items.length > 0 ? items : undefined;
}

function addTextsWithMetadata(
  onAddTexts: (texts: string[], aiTextMetadata?: AiTextMetadataPayload[]) => Promise<void>,
  texts: string[],
  metadata?: AiTextMetadataPayload[],
) {
  return metadata?.length ? onAddTexts(texts, metadata) : onAddTexts(texts);
}

export function useAiTextGeneration(params: AiTextGenerationParams) {
  const { countryKey, onAddTexts, onSaveGeneratedAndSelectOnly, onMarkTextsForHide, onSaveGeneratedPerDrop, onPerDropSmsTextsApplied, onAiGeneratedDeselectOld, onAfterSave: _onAfterSave, onHideTextsAfterUseAndHide } = params;
  const { success: toastOk, error: toastErr } = useToast();
  const promptPreview = useGenerationPromptPreview();
  const [generatedTexts, setGeneratedTexts] = useState<string[]>([]);
  const [generatedTranslations, setGeneratedTranslations] = useState<(string | null)[] | null>(null);
  const [perDropBatches, setPerDropBatches] = useState<string[][] | null>(null);
  const [perDropTranslations, setPerDropTranslations] = useState<(string | null)[][] | null>(null);
  const [generatedMetadata, setGeneratedMetadata] = useState<AiGeneratedTextMetadata | null>(null);
  const [perDropMetadata, setPerDropMetadata] = useState<AiGeneratedTextMetadata[]>([]);
  const [perDropApplied, setPerDropApplied] = useState(false);
  const [perDropGenerationTimeMs, setPerDropGenerationTimeMs] = useState<number | undefined>();
  const [lastGroupId, setLastGroupId] = useState('');
  const [groupIdsPerDrop, setGroupIdsPerDrop] = useState<string[]>([]);
  const regenMutation = useGenerateTexts();
  const translateMutation = useTranslateTexts();

  const [amountPerDrop, setAmountPerDrop] = useState(15);

  const handleDevCopyExactModelPrompt = useCallback(async () => {
    const spec = resolveAiGeneratePreviewParams({
      lastGroupId,
      groupIdsPerDrop,
      countryKey,
      perDropBatches,
      generatedTexts,
      amountPerDrop,
    });
    if (!spec) {
      toastErr('Nothing to copy', 'Generate texts first so a group is known.');
      return;
    }
    try {
      const data = await promptPreview.mutateAsync(spec);
      await navigator.clipboard.writeText(formatGenerationPromptForClipboard(data));
      toastOk('Copied', 'Exact system + user text for this preview.');
    } catch (err) {
      toastErr('Copy failed', getApiErrorMessage(err, 'Could not load prompt preview.'));
    }
  }, [
    lastGroupId,
    groupIdsPerDrop,
    countryKey,
    perDropBatches,
    generatedTexts,
    amountPerDrop,
    promptPreview,
    toastOk,
    toastErr,
  ]);

  const handleAiGenerated = useCallback(
    (
      texts: string[] | string[][],
      gId: string,
      mode: AiGenerateMode,
      durationMs?: number,
      translations?: string[] | string[][],
      idsPerDrop?: string[],
      amtPerDrop?: number,
      aiTextMetadata?: AiGeneratedTextMetadata | AiGeneratedTextMetadata[],
    ) => {
      onAiGeneratedDeselectOld?.();
      setLastGroupId(gId);
      setGroupIdsPerDrop(idsPerDrop ?? []);
      if (amtPerDrop != null) setAmountPerDrop(amtPerDrop);
      setPerDropApplied(false);
      if (mode === 'perDrop' && Array.isArray(texts[0])) {
        setPerDropBatches(texts as string[][]);
        setPerDropTranslations(
          translations && Array.isArray(translations[0])
            ? (translations as string[][]).map((t) => t.map((s) => s as string | null))
            : null,
        );
        setPerDropMetadata(Array.isArray(aiTextMetadata) ? aiTextMetadata : []);
        setGeneratedMetadata(null);
        setPerDropGenerationTimeMs(durationMs);
        setGeneratedTexts([]);
        setGeneratedTranslations(null);
      } else {
        setGeneratedTexts(texts as string[]);
        setGeneratedTranslations(
          translations && Array.isArray(translations) && !Array.isArray(translations[0])
            ? (translations as string[]).map((s) => s as string | null)
            : null,
        );
        setGeneratedMetadata(Array.isArray(aiTextMetadata) ? null : (aiTextMetadata ?? null));
        setPerDropMetadata([]);
        setPerDropBatches(null);
        setPerDropTranslations(null);
        setPerDropGenerationTimeMs(undefined);
      }
    },
    [onAiGeneratedDeselectOld],
  );

  const handleSaveGenerated = useCallback(async (texts: string[]) => {
    onSaveGeneratedAndSelectOnly?.(texts);
    await addTextsWithMetadata(onAddTexts, texts, withTextMetadata(texts, generatedMetadata));
    setGeneratedTexts([]);
    setGeneratedTranslations(null);
    setGeneratedMetadata(null);
    /* Do not call onAfterSave — keep campaign texts section open. */
  }, [generatedMetadata, onAddTexts, onSaveGeneratedAndSelectOnly]);

  const handleSaveAndHide = useCallback(
    async (selectedTexts: string[]) => {
      onSaveGeneratedAndSelectOnly?.(selectedTexts);
      await addTextsWithMetadata(
        onAddTexts,
        selectedTexts,
        withTextMetadata(selectedTexts, generatedMetadata),
      );
      if (selectedTexts.length > 0) onMarkTextsForHide?.(selectedTexts);
      if (selectedTexts.length > 0) await onHideTextsAfterUseAndHide?.(selectedTexts);
      setGeneratedTexts([]);
      setGeneratedTranslations(null);
      setGeneratedMetadata(null);
      /* Do not call onAfterSave — keep campaign texts section open. */
    },
    [generatedMetadata, onAddTexts, onSaveGeneratedAndSelectOnly, onMarkTextsForHide, onHideTextsAfterUseAndHide],
  );

  const handleRegenerate = useCallback((indices: number[]) => {
    if (!lastGroupId || indices.length === 0) return;
    regenMutation.mutate(
      { groupId: lastGroupId, countryKey, amount: indices.length },
      {
        onSuccess: (data) => {
          const replaceSet = new Set(indices);
          setGeneratedTexts((prev) => {
            let ri = 0;
            return prev.map((text, i) => {
              if (replaceSet.has(i) && ri < data.texts.length) return data.texts[ri++];
              return text;
            });
          });
          setGeneratedTranslations((prev) => {
            if (!prev) return null;
            const next = [...prev];
            for (const i of indices) next[i] = null;
            return next;
          });
        },
        onError: (err) => toastErr('Regeneration failed', getApiErrorMessage(err, 'The AI request failed.')),
      },
    );
  }, [lastGroupId, countryKey, regenMutation, toastErr]);

  const handleRegenerateOverLength = useCallback(() => {
    const indices = buildIndicesOverSmsLimit(generatedTexts);
    if (indices.length === 0) return;
    handleRegenerate(indices);
  }, [generatedTexts, handleRegenerate]);

  const handleDismiss = useCallback(() => {
    setGeneratedTexts([]);
    setGeneratedTranslations(null);
    setGeneratedMetadata(null);
    setPerDropBatches(null);
    setPerDropTranslations(null);
    setPerDropMetadata([]);
  }, []);

  const handleSavePerDrop = useCallback(
    async (batches: string[][]) => {
      if (!onSaveGeneratedPerDrop) return;
      const allTexts = batches.flat();
      await addTextsWithMetadata(onAddTexts, allTexts, perDropTextMetadata(batches, perDropMetadata));
      await onSaveGeneratedPerDrop(batches);
      setPerDropBatches(null);
      setPerDropMetadata([]);
      /* Do not call onAfterSave — keep campaign texts section open. */
    },
    [onAddTexts, onSaveGeneratedPerDrop, perDropMetadata],
  );

  const handleDismissPerDrop = useCallback(() => {
    setPerDropBatches(null);
    setPerDropTranslations(null);
    setPerDropMetadata([]);
    setPerDropApplied(false);
    setGroupIdsPerDrop([]);
    setPerDropGenerationTimeMs(undefined);
  }, []);

  const handleRegeneratePerDrop = useCallback(
    (dropIdx: number, indices: number[]) => {
      const gId = groupIdsPerDrop[dropIdx] ?? lastGroupId;
      if (!gId || indices.length === 0 || !perDropBatches?.[dropIdx]) return;
      regenMutation.mutate(
        { groupId: gId, countryKey, amount: indices.length },
        {
          onSuccess: (data) => {
            setPerDropBatches((prev) => {
              if (!prev) return prev;
              const batch = [...prev[dropIdx]];
              let ri = 0;
              for (const i of indices) {
                if (ri < data.texts.length) batch[i] = data.texts[ri++];
              }
              const next = [...prev];
              next[dropIdx] = batch;
              return next;
            });
            setPerDropTranslations((p) => {
              if (!p) return null;
              const next = p.map((batch) => [...batch]);
              const batch = next[dropIdx];
              if (!batch) return p;
              for (const i of indices) batch[i] = null;
              return next;
            });
          },
          onError: (err) => toastErr('Regeneration failed', getApiErrorMessage(err, 'The AI request failed.')),
        },
      );
    },
    [lastGroupId, groupIdsPerDrop, countryKey, regenMutation, perDropBatches, toastErr],
  );

  const handleRegenerateFailedPerDrop = useCallback(
    async (failedDropIndices: number[]) => {
      if (failedDropIndices.length === 0 || !perDropBatches) return;
      const amt = amountPerDrop || 15;
      for (const dropIdx of failedDropIndices) {
        const gId = groupIdsPerDrop[dropIdx] ?? lastGroupId;
        if (!gId) continue;
        const data = await regenMutation.mutateAsync({
          groupId: gId,
          countryKey,
          amount: amt,
        });
        setPerDropBatches((prev) => {
          if (!prev) return prev;
          const next = [...prev];
          next[dropIdx] = data.texts;
          return next;
        });
      }
      setPerDropTranslations((p) => {
        if (!p) return null;
        const next = p.map((b) => [...b]);
        for (const di of failedDropIndices) {
          if (next[di]) next[di] = next[di]!.map(() => null);
        }
        return next;
      });
    },
    [lastGroupId, groupIdsPerDrop, countryKey, amountPerDrop, regenMutation, perDropBatches],
  );

  const handleRegenerateAllPerDrop = useCallback(
    async (dropIndices: Array<{ dropIdx: number; indices: number[] }>) => {
      if (dropIndices.length === 0) return;
      for (const { dropIdx, indices } of dropIndices) {
        if (indices.length === 0) continue;
        const gId = groupIdsPerDrop[dropIdx] ?? lastGroupId;
        if (!gId) continue;
        const data = await regenMutation.mutateAsync({
          groupId: gId,
          countryKey,
          amount: indices.length,
        });
        setPerDropBatches((prev) => {
          if (!prev) return prev;
          const batch = [...prev[dropIdx]];
          let ri = 0;
          for (const i of indices) {
            if (ri < data.texts.length) batch[i] = data.texts[ri++];
          }
          const next = [...prev];
          next[dropIdx] = batch;
          return next;
        });
      }
      setPerDropTranslations((p) => {
        if (!p) return null;
        const next = p.map((batch) => [...batch]);
        for (const { dropIdx: di, indices: idxs } of dropIndices) {
          const b = next[di];
          if (b) for (const i of idxs) b[i] = null;
        }
        return next;
      });
    },
    [lastGroupId, groupIdsPerDrop, countryKey, regenMutation],
  );

  const handleRegenerateOverLengthPerDrop = useCallback(async () => {
    if (!perDropBatches) return;
    const specs = buildDropIndicesOverSmsLimit(perDropBatches);
    if (specs.length === 0) return;
    await handleRegenerateAllPerDrop(specs);
  }, [perDropBatches, handleRegenerateAllPerDrop]);

  const handleTranslateAll = useCallback(() => {
    if (generatedTexts.length === 0) return;
    translateMutation.mutate(
      { texts: generatedTexts },
      {
        onSuccess: (data) => {
          setGeneratedTranslations(data.translations.map((t) => t as string | null));
        },
        onError: (err) => toastErr('Translation failed', getApiErrorMessage(err, 'The AI request failed.')),
      },
    );
  }, [generatedTexts, translateMutation, toastErr]);

  const handleTranslateMissing = useCallback(() => {
    if (generatedTexts.length === 0 || !generatedTranslations) return;
    const missingIndices = generatedTranslations
      .map((t, i) => (t == null ? i : -1))
      .filter((i) => i >= 0);
    if (missingIndices.length === 0) return;
    const textsToTranslate = missingIndices.map((i) => generatedTexts[i]!);
    translateMutation.mutate(
      { texts: textsToTranslate },
      {
        onSuccess: (data) => {
          setGeneratedTranslations((prev) => {
            if (!prev) return data.translations.map((t) => t as string | null);
            const next = [...prev];
            missingIndices.forEach((idx, j) => {
              if (j < data.translations.length) next[idx] = data.translations[j];
            });
            return next;
          });
        },
      },
    );
  }, [generatedTexts, generatedTranslations, translateMutation]);

  const handleTranslatePerDropAll = useCallback(
    (dropIdx: number) => {
      const batch = perDropBatches?.[dropIdx];
      if (!batch?.length) return;
      translateMutation.mutate(
        { texts: batch },
        {
          onSuccess: (data) => {
            setPerDropTranslations((prev) => {
              const next = prev ? prev.map((b) => [...b]) : perDropBatches!.map(() => []);
              next[dropIdx] = data.translations.map((t) => t as string | null);
              return next;
            });
          },
        },
      );
    },
    [perDropBatches, translateMutation],
  );

  const handleTranslatePerDropMissing = useCallback(
    (dropIdx: number) => {
      const batch = perDropBatches?.[dropIdx];
      const trans = perDropTranslations?.[dropIdx];
      if (!batch?.length || !trans) return;
      const missingIndices = trans.map((t, i) => (t == null ? i : -1)).filter((i) => i >= 0);
      if (missingIndices.length === 0) return;
      const textsToTranslate = missingIndices.map((i) => batch[i]!);
      translateMutation.mutate(
        { texts: textsToTranslate },
        {
          onSuccess: (data) => {
            setPerDropTranslations((prev) => {
              if (!prev) return prev;
              const next = prev.map((b) => [...b]);
              const target = next[dropIdx];
              if (!target) return prev;
              missingIndices.forEach((idx, j) => {
                if (j < data.translations.length) target[idx] = data.translations[j];
              });
              return next;
            });
          },
        },
      );
    },
    [perDropBatches, perDropTranslations, translateMutation],
  );

  const handleTranslateAllPerDrop = useCallback(async () => {
    if (!perDropBatches?.length) return;
    const items: { dropIdx: number; textIdx: number; text: string }[] = [];
    for (let di = 0; di < perDropBatches.length; di++) {
      const batch = perDropBatches[di]!;
      const trans = perDropTranslations?.[di];
      for (let ti = 0; ti < batch.length; ti++) {
        if (!trans || trans[ti] == null) items.push({ dropIdx: di, textIdx: ti, text: batch[ti]! });
      }
    }
    if (items.length === 0) return;
    const BATCH_SIZE = 15;
    for (let i = 0; i < items.length; i += BATCH_SIZE) {
      const chunk = items.slice(i, i + BATCH_SIZE);
      const data = await translateMutation.mutateAsync({ texts: chunk.map((x) => x.text) });
      setPerDropTranslations((prev) => {
        const next = prev ? prev.map((b) => [...b]) : perDropBatches!.map(() => []);
        chunk.forEach(({ dropIdx, textIdx }, j) => {
          const target = next[dropIdx];
          if (target && j < data.translations.length) target[textIdx] = data.translations[j];
        });
        return next;
      });
    }
  }, [perDropBatches, perDropTranslations, translateMutation]);

  const handleSaveAndHidePerDrop = useCallback(
    async (batches: string[][], _unselectedTexts: string[]) => {
      if (!onSaveGeneratedPerDrop) return;
      const allTexts = batches.flat();
      onSaveGeneratedAndSelectOnly?.(allTexts);
      onPerDropSmsTextsApplied?.(batches);
      await addTextsWithMetadata(onAddTexts, allTexts, perDropTextMetadata(batches, perDropMetadata));
      await onSaveGeneratedPerDrop(batches);
      if (allTexts.length > 0) onMarkTextsForHide?.(allTexts);
      setPerDropBatches(batches);
      setPerDropApplied(true);
      if (allTexts.length > 0) await onHideTextsAfterUseAndHide?.(allTexts);
      /* Do not call onAfterSave — keep campaign texts section open. */
    },
    [onAddTexts, onSaveGeneratedAndSelectOnly, onSaveGeneratedPerDrop, onPerDropSmsTextsApplied, onMarkTextsForHide, onHideTextsAfterUseAndHide, perDropMetadata],
  );

  return {
    generatedTexts,
    generatedTranslations,
    perDropBatches,
    perDropTranslations,
    perDropApplied,
    perDropGenerationTimeMs,
    groupIdsPerDrop,
    lastGroupId,
    isRegenerating: regenMutation.isPending,
    isTranslating: translateMutation.isPending,
    handleAiGenerated,
    handleTranslateAll,
    handleTranslateMissing,
    handleTranslatePerDropAll,
    handleTranslatePerDropMissing,
    handleTranslateAllPerDrop,
    handleSaveGenerated,
    handleSaveAndHide,
    handleRegenerate,
    handleDismiss,
    handleSavePerDrop,
    handleSaveAndHidePerDrop,
    handleDismissPerDrop,
    handleRegeneratePerDrop,
    handleRegenerateAllPerDrop,
    handleRegenerateOverLength,
    handleRegenerateOverLengthPerDrop,
    handleRegenerateFailedPerDrop,
    handleDevCopyExactModelPrompt,
    isDevCopyExactPromptPending: promptPreview.isPending,
  };
}
